/**
 * Charte graphique et styles des visuels et supports (recette du 31/10/2026).
 *  - `OngletCharte` (Organisme → Charte graphique, administrateur) : style par défaut avec vignettes, couleurs
 *    personnalisables avec contrôle du contraste (AA), police, intensité, mode de rendu, gabarit de support,
 *    ajustement permis aux formateurs, plafond d'images Gemini, logo sur les supports.
 *  - `StyleDuSupport` (Visuels d'une formation, formateur) : style, gabarit, mode ; couleurs si l'organisme l'autorise.
 *  - `GalerieStyles` (administrateur) : le même visuel dans chaque style, en vectoriel et illustré.
 */
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Download, Images, Palette, Sparkles } from "lucide-react";
import type { vueCharte, lireStyleDeSupport } from "@/serveur/services/charte";
import { contraste, type Charte, type RoleCouleur } from "@/domaine/visuels/charte";
import { api, ErreurApi, type Json } from "../api";
import { Alerte, Bouton, Carte, Chargement, cx, Etiquette, Selecteur, TitrePage, useNotifier, useTitreDocument } from "../ui/base";

type VueCharte = Json<Awaited<ReturnType<typeof vueCharte>>>;
type VueStyleSupport = Json<Awaited<ReturnType<typeof lireStyleDeSupport>>>;

const TYPES_DEMO = [
  ["processus", "Processus"],
  ["cycle", "Cycle"],
  ["pyramide", "Pyramide"],
  ["matrice_2x2", "Matrice"],
  ["carte_conceptuelle", "Carte"],
  ["frise", "Frise"],
  ["tableau_comparatif", "Tableau"],
  ["graphique", "Graphique"],
] as const;

export const useCharte = () => useQuery({ queryKey: ["charte"], queryFn: () => api.get<VueCharte>("/charte") });

/** Badge de contraste WCAG : « 7,2:1 AA » (vert) ou « 3,1:1 insuffisant » (rouge). */
export function BadgeContraste({ a, b, seuil = 4.5 }: { a: string; b: string; seuil?: number }) {
  const r = contraste(a, b);
  const ok = r >= seuil;
  return (
    <span className={cx("inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold", ok ? "bg-emerald-100 text-emerald-900" : "bg-rose-100 text-rose-900")} title={`Contraste ${r.toFixed(2)}:1 (seuil ${seuil}:1)`}>
      {r.toFixed(1).replace(".", ",")}:1 {ok ? "AA" : "insuffisant"}
    </span>
  );
}

/** Carte de choix d'un style : vignette rendue par le serveur, nom, description, pastilles de palette. */
function CarteStyle({ s, actif, choisir, type = "processus", charte = "1" }: { s: VueCharte["catalogue"]["styles"][number]; actif: boolean; choisir: () => void; type?: string; charte?: "0" | "1" }) {
  return (
    <button type="button" onClick={choisir} aria-pressed={actif} data-testid={`style-${s.id}`} className={cx("group overflow-hidden rounded-lg border-2 bg-carte text-left transition", actif ? "border-accent shadow-md" : "border-trait hover:border-encre-3")}>
      <img src={`/api/styles/${s.id}/apercu.png?type=${type}&largeur=480&charte=${charte}`} alt={`Aperçu du style ${s.nom}`} className="block aspect-video w-full bg-papier-2 object-cover" loading="lazy" />
      <div className="space-y-1 px-3 py-2">
        <p className="flex items-center justify-between gap-2 text-[13.5px] font-semibold">
          {s.nom}
          {actif && <Check className="size-4 text-accent" aria-hidden />}
        </p>
        <p className="text-[12px] leading-snug text-encre-2">{s.description}</p>
        <p className="flex gap-1 pt-1" aria-hidden>
          {Object.values(s.palette).map((c, i) => (
            <span key={i} className="size-4 rounded-full border border-black/10" style={{ background: c }} />
          ))}
        </p>
      </div>
    </button>
  );
}

function CarteGabarit({ g, actif, choisir, style }: { g: VueCharte["catalogue"]["gabarits"][number]; actif: boolean; choisir: () => void; style?: string }) {
  return (
    <button type="button" onClick={choisir} aria-pressed={actif} data-testid={`gabarit-${g.id}`} className={cx("overflow-hidden rounded-lg border-2 bg-carte text-left transition", actif ? "border-accent shadow-md" : "border-trait hover:border-encre-3")}>
      <img src={`/api/gabarits/${g.id}/apercu.png?page=0&largeur=480${style ? `&style=${style}` : ""}`} alt={`Couverture du gabarit ${g.nom}`} className="block aspect-video w-full bg-papier-2 object-cover" loading="lazy" />
      <div className="px-3 py-2">
        <p className="flex items-center justify-between text-[13.5px] font-semibold">
          {g.nom}
          {actif && <Check className="size-4 text-accent" aria-hidden />}
        </p>
        <p className="text-[12px] leading-snug text-encre-2">{g.description}</p>
      </div>
    </button>
  );
}

function SelecteurCouleur({ role, libelle, usage, valeur, defaut, changer, fond }: { role: RoleCouleur; libelle: string; usage: string; valeur: string; defaut: string; changer: (c: string) => void; fond: string }) {
  const effective = valeur || defaut;
  const contre = fond;
  return (
    <label className="flex items-center gap-3 rounded-md border border-trait bg-carte px-3 py-2">
      <input type="color" value={effective} onChange={(e) => changer(e.target.value.toUpperCase())} className="h-9 w-12 cursor-pointer rounded border border-trait" aria-label={`Couleur ${libelle}`} data-testid={`couleur-${role}`} />
      <span className="min-w-0 flex-1">
        <span className="block text-[13px] font-semibold">
          {libelle} <span className="font-mono text-[11.5px] font-normal text-encre-3">{effective}</span>
        </span>
        <span className="block truncate text-[11.5px] text-encre-3">{usage}</span>
      </span>
      {role === "encre" ? <BadgeContraste a={effective} b={contre} /> : role !== "fond" ? <span className="text-[11px] text-encre-3" title="Quand cette couleur porte un texte, la plateforme l'assombrit juste assez pour garantir 4,5:1.">texte auto 4,5:1</span> : null}
      {valeur && (
        <button type="button" className="text-[11.5px] text-encre-3 underline" onClick={() => changer("")}>
          Style
        </button>
      )}
    </label>
  );
}

// ——— Organisme → Charte graphique ———

export function OngletCharte() {
  const requetes = useQueryClient();
  const notifier = useNotifier();
  const vue = useCharte();
  const [c, setC] = useState<Charte | null>(null);
  useEffect(() => {
    if (vue.data && !c) setC(vue.data.charte as Charte);
  }, [vue.data, c]);
  const enregistrer = useMutation({
    mutationFn: (charte: Charte) => api.put<VueCharte>("/admin/charte", charte),
    onSuccess: (r) => {
      requetes.setQueryData(["charte"], r);
      setC(r.charte as Charte);
      notifier("succes", r.theme.corrections.length ? "Charte enregistrée — une couleur a été corrigée pour rester lisible." : "Charte graphique enregistrée.");
    },
  });
  if (vue.isPending || !c) return <Chargement />;
  if (vue.error) return <Alerte ton="danger">{vue.error.message}</Alerte>;
  const { catalogue, images } = vue.data;
  const style = catalogue.styles.find((s) => s.id === c.style)!;
  const poser = (x: Partial<Charte>) => setC({ ...c, ...x });
  const erreurs = enregistrer.error instanceof ErreurApi ? (enregistrer.error.details?.erreurs as string[] | undefined) : undefined;
  return (
    <div className="space-y-6" data-testid="onglet-charte">
      <Alerte ton="info" titre="Une charte, des supports cohérents et vifs">
        Le style choisi s'applique aux visuels générés (illustrés par Gemini ou dessinés en vectoriel) et aux diapositives des supports PPTX : couleurs du thème, polices, bandeaux, logo. Les formateurs choisissent le style et le gabarit de chaque support ; ils n'ajustent les couleurs que si vous l'autorisez.{" "}
        <a className="font-medium underline" href="/admin/galerie-styles">
          Ouvrir la galerie des styles
        </a>
      </Alerte>

      <section className="space-y-3">
        <h3 className="flex items-center gap-2 text-sm font-semibold">
          <Palette className="size-4" aria-hidden /> Style par défaut
        </h3>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {catalogue.styles.map((s) => (
            <CarteStyle key={s.id} s={s} actif={c.style === s.id} choisir={() => poser({ style: s.id as Charte["style"] })} charte="0" />
          ))}
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-[1.2fr_1fr]">
        <div className="space-y-3">
          <h3 className="text-sm font-semibold">Couleurs de la charte</h3>
          <p className="text-[12.5px] text-encre-2">Sans choix, les couleurs du style « {style.nom} » s'appliquent. Le texte doit contraster d'au moins 4,5:1 avec le fond (WCAG AA) : sinon il est corrigé à l'enregistrement. Les couleurs vives qui portent un texte (pastilles, en-têtes) sont assombries automatiquement juste assez pour rester lisibles.</p>
          <div className="grid gap-2">
            {catalogue.roles.map((r) => (
              <SelecteurCouleur key={r.id} role={r.id} libelle={r.libelle} usage={r.usage} valeur={c.couleurs[r.id] ?? ""} defaut={style.palette[r.id]} fond={c.couleurs.fond || style.palette.fond} changer={(x) => poser({ couleurs: { ...c.couleurs, [r.id]: x } })} />
            ))}
          </div>
        </div>
        <div className="space-y-4">
          <h3 className="text-sm font-semibold">Typographie, intensité, rendu</h3>
          <Selecteur libelle="Police" value={c.police} onChange={(e) => poser({ police: e.target.value as Charte["police"] })} aide="Polices embarquées côté serveur pour les visuels ; dans PowerPoint, installez-la (Google Fonts) ou elle sera remplacée.">
            <option value="">Police du style ({style.police})</option>
            {catalogue.polices.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </Selecteur>
          <fieldset>
            <legend className="mb-1 text-[13px] font-medium">Intensité des couleurs</legend>
            <div className="flex gap-2">
              {(["douce", "vive", "intense"] as const).map((i) => (
                <Bouton key={i} taille="sm" variante={c.intensite === i ? "primaire" : "secondaire"} onClick={() => poser({ intensite: i })}>
                  {i === "douce" ? "Douce" : i === "vive" ? "Vive" : "Intense"}
                </Bouton>
              ))}
            </div>
          </fieldset>
          <fieldset>
            <legend className="mb-1 text-[13px] font-medium">Mode de rendu par défaut des visuels</legend>
            <div className="flex flex-wrap gap-2">
              {catalogue.modes.map((m) => (
                <Bouton key={m.id} taille="sm" variante={c.mode === m.id ? "primaire" : "secondaire"} onClick={() => poser({ mode: m.id as Charte["mode"] })}>
                  {m.libelle}
                </Bouton>
              ))}
            </div>
            <p className="mt-1 text-[12px] text-encre-3">
              {images.disponible ? `Illustrations : ${images.description}. ` : "Aucune clé Gemini : le rendu vectoriel s'applique. "}
              {catalogue.cout_image.texte}. Le texte des visuels est toujours posé par le serveur (jamais par Gemini), et un graphique chiffré reste vectoriel.
            </p>
          </fieldset>
          <label className="block text-[13px] font-medium">
            Plafond d'images Gemini par formation
            <input type="number" min={0} max={1000} value={c.plafond_images} onChange={(e) => poser({ plafond_images: Math.max(0, Number(e.target.value) || 0) })} className="mt-1 block w-32 rounded-md border border-trait bg-carte px-2 py-1.5" data-testid="plafond-images" />
            <span className="block text-[12px] font-normal text-encre-3">≈ {(c.plafond_images * catalogue.cout_image.usd).toFixed(2).replace(".", ",")} $ au plus par formation.</span>
          </label>
          <label className="flex items-start gap-2 text-[13px]">
            <input type="checkbox" checked={c.ajustement_formateur} onChange={(e) => poser({ ajustement_formateur: e.target.checked })} className="mt-0.5" data-testid="ajustement-formateur" />
            <span>Autoriser les formateurs à ajuster les couleurs de leurs supports (contraste toujours contrôlé)</span>
          </label>
          <label className="flex items-start gap-2 text-[13px]">
            <input type="checkbox" checked={c.logo_sur_supports} onChange={(e) => poser({ logo_sur_supports: e.target.checked })} className="mt-0.5" />
            <span>Afficher le logo de l'organisme (onglet Identité) sur les supports</span>
          </label>
        </div>
      </section>

      <section className="space-y-3">
        <h3 className="text-sm font-semibold">Gabarit de support par défaut</h3>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {catalogue.gabarits.map((g) => (
            <div key={g.id} className="space-y-1">
              <CarteGabarit g={g} actif={c.gabarit === g.id} choisir={() => poser({ gabarit: g.id as Charte["gabarit"] })} />
              <a className="inline-flex items-center gap-1 text-[12px] font-medium text-accent underline-offset-4 hover:underline" href={`/api/gabarits/${g.id}/exemple.pptx`}>
                <Download className="size-3.5" aria-hidden /> PPTX d'exemple
              </a>
            </div>
          ))}
        </div>
      </section>

      {erreurs && (
        <Alerte ton="danger" titre="Charte invalide">
          <ul className="list-disc pl-4">{erreurs.map((x) => <li key={x}>{x}</li>)}</ul>
        </Alerte>
      )}
      {vue.data.theme.corrections.length > 0 && <Alerte ton="attention" titre="Correction de contraste appliquée">{vue.data.theme.corrections.join(" ")}</Alerte>}
      <div className="flex gap-2">
        <Bouton variante="primaire" enCours={enregistrer.isPending} onClick={() => enregistrer.mutate(c)} data-testid="enregistrer-charte">
          Enregistrer la charte
        </Bouton>
        <Bouton onClick={() => setC(vue.data.charte as Charte)}>Annuler</Bouton>
      </div>
    </div>
  );
}

// ——— « Style de ce support » (formation) ———

export function StyleDuSupport({ formationId, apresChangement }: { formationId: string; apresChangement?: () => void }) {
  const requetes = useQueryClient();
  const notifier = useNotifier();
  const vue = useCharte();
  const style = useQuery({ queryKey: ["style-support", formationId], queryFn: () => api.get<VueStyleSupport>(`/formations/${formationId}/style-support`) });
  const enregistrer = useMutation({
    mutationFn: (corps: VueStyleSupport["style"]) => api.put<VueStyleSupport>(`/formations/${formationId}/style-support`, corps),
    onSuccess: (r) => {
      requetes.setQueryData(["style-support", formationId], r);
      apresChangement?.();
      notifier("succes", "Style du support enregistré : il s'applique aux prochains visuels et supports.");
    },
    onError: (e) => notifier("danger", e.message),
  });
  if (vue.isPending || style.isPending) return <Chargement />;
  if (vue.error || style.error) return <Alerte ton="danger">{(vue.error ?? style.error)!.message}</Alerte>;
  const { catalogue } = vue.data;
  const s = style.data;
  const poser = (x: Partial<VueStyleSupport["style"]>) => enregistrer.mutate({ ...s.style, ...x });
  return (
    <details className="rounded-md border border-trait bg-papier-2/40 p-3" open data-testid="style-du-support">
      <summary className="cursor-pointer text-sm font-semibold">
        Style de ce support <Etiquette ton="accent">{catalogue.styles.find((x) => x.id === s.effectif.style)?.nom}</Etiquette> <Etiquette>{catalogue.gabarits.find((x) => x.id === s.effectif.gabarit)?.nom}</Etiquette> <Etiquette>{catalogue.modes.find((x) => x.id === s.effectif.mode)?.libelle}</Etiquette>
      </summary>
      <div className="mt-3 space-y-4">
        <div className="flex flex-wrap items-center gap-2 text-[12.5px]">
          <span className="font-medium">Mode :</span>
          {catalogue.modes.map((m) => (
            <Bouton key={m.id} taille="sm" variante={s.effectif.mode === m.id ? "primaire" : "secondaire"} onClick={() => poser({ mode: m.id as "illustre" | "vectoriel" })}>
              {m.libelle}
            </Bouton>
          ))}
          <span className="text-encre-3">
            Images Gemini : {s.images.produites} / {s.images.plafond} pour cette formation — {catalogue.cout_image.texte}.
          </span>
        </div>
        <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
          {catalogue.styles.map((x) => (
            <CarteStyle key={x.id} s={x} actif={s.effectif.style === x.id} choisir={() => poser({ style: x.id as "s4m-vif" })} />
          ))}
        </div>
        <div>
          <p className="mb-2 text-[13px] font-medium">Gabarit des diapositives</p>
          <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
            {catalogue.gabarits.map((g) => (
              <CarteGabarit key={g.id} g={g} style={s.effectif.style} actif={s.effectif.gabarit === g.id} choisir={() => poser({ gabarit: g.id as "moderne-vif" })} />
            ))}
          </div>
        </div>
        {s.ajustement_couleurs ? (
          <div className="grid gap-2 md:grid-cols-3">
            {(["primaire", "secondaire", "accent"] as const).map((r) => (
              <SelecteurCouleur key={r} role={r} libelle={catalogue.roles.find((x) => x.id === r)!.libelle} usage="Ajustement de ce support" valeur={s.style.couleurs?.[r] ?? ""} defaut={s.effectif.c[r]} fond={s.effectif.c.fond} changer={(c) => poser({ couleurs: { ...(s.style.couleurs ?? {}), [r]: c } })} />
            ))}
          </div>
        ) : (
          <p className="text-[12px] text-encre-3">Couleurs : celles de la charte de l'organisme (l'ajustement par les formateurs n'est pas autorisé).</p>
        )}
      </div>
    </details>
  );
}

// ——— Galerie des styles (administrateur) ———

export function GalerieStyles() {
  useTitreDocument("Galerie des styles");
  const notifier = useNotifier();
  const vue = useCharte();
  const [type, setType] = useState<string>("processus");
  const [version, setVersion] = useState(0);
  const illustrer = useMutation({
    mutationFn: () => api.post<{ faits: string[]; echecs: string[]; cout: string }>("/admin/galerie/illustrer", { type }),
    onSuccess: (r) => {
      setVersion((v) => v + 1);
      notifier(r.echecs.length ? "danger" : "succes", `${r.faits.length} aperçu(s) illustré(s) — ${r.cout}${r.echecs.length ? ` — ${r.echecs.length} échec(s)` : ""}.`);
    },
    onError: (e) => notifier("danger", e.message),
  });
  const cout = useMemo(() => (vue.data ? (vue.data.catalogue.styles.length * vue.data.catalogue.cout_image.usd).toFixed(2).replace(".", ",") : ""), [vue.data]);
  if (vue.isPending) return <Chargement />;
  if (vue.error) return <Alerte ton="danger">{vue.error.message}</Alerte>;
  const { catalogue, images } = vue.data;
  return (
    <div className="space-y-5">
      <TitrePage titre="Galerie des styles" soustitre="Le même visuel dans chaque style du catalogue, aux couleurs de votre charte : schéma vectoriel (gratuit) et version illustrée par Gemini (sur demande)." />
      <div className="flex flex-wrap items-center gap-2">
        {TYPES_DEMO.map(([id, libelle]) => (
          <Bouton key={id} taille="sm" variante={type === id ? "primaire" : "secondaire"} onClick={() => setType(id)}>
            {libelle}
          </Bouton>
        ))}
        <span className="flex-1" />
        <Bouton icone={<Sparkles className="size-4" aria-hidden />} disabled={!images.disponible || type === "graphique"} enCours={illustrer.isPending} onClick={() => illustrer.mutate()} title={type === "graphique" ? "Un graphique chiffré reste toujours vectoriel." : undefined}>
          Produire les aperçus illustrés ({catalogue.styles.length} images ≈ {cout} $)
        </Bouton>
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2" data-testid="galerie-styles">
        {catalogue.styles.map((s) => (
          <Carte key={s.id} className="overflow-hidden p-0">
            <div className="flex items-center justify-between border-b border-trait px-4 py-2">
              <p className="text-sm font-semibold">{s.nom}</p>
              <p className="text-[12px] text-encre-3">{s.police}</p>
            </div>
            <div className="grid grid-cols-1 gap-px bg-trait sm:grid-cols-2">
              <figure className="bg-carte">
                <img src={`/api/styles/${s.id}/apercu.png?type=${type}&largeur=720`} alt={`${s.nom} — schéma vectoriel`} className="block aspect-video w-full object-cover" loading="lazy" />
                <figcaption className="px-3 py-1 text-[11.5px] text-encre-3">Schéma (vectoriel)</figcaption>
              </figure>
              <figure className="bg-carte">
                <img key={version} src={`/api/styles/${s.id}/apercu.png?type=${type}&largeur=720&mode=illustre&v=${version}`} alt={`${s.nom} — illustré`} className="block aspect-video w-full object-cover" loading="lazy" />
                <figcaption className="px-3 py-1 text-[11.5px] text-encre-3">Illustré (Gemini) — {images.disponible ? "après « Produire les aperçus illustrés »" : "clé Gemini requise"}</figcaption>
              </figure>
            </div>
          </Carte>
        ))}
      </div>
      <section className="space-y-3">
        <h2 className="flex items-center gap-2 text-base font-semibold">
          <Images className="size-4" aria-hidden /> Gabarits de supports
        </h2>
        {catalogue.gabarits.map((g) => (
          <Carte key={g.id} className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold">
                {g.nom} <span className="font-normal text-encre-3">— {g.description}</span>
              </p>
              <a className="inline-flex items-center gap-1 text-[12.5px] font-medium text-accent hover:underline" href={`/api/gabarits/${g.id}/exemple.pptx`}>
                <Download className="size-3.5" aria-hidden /> PPTX d'exemple
              </a>
            </div>
            <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
              {[0, 4, 6, 8].map((p) => (
                <img key={p} src={`/api/gabarits/${g.id}/apercu.png?page=${p}&largeur=480`} alt={`${g.nom}, diapositive ${p + 1}`} className="block aspect-video w-full rounded border border-trait object-cover" loading="lazy" />
              ))}
            </div>
          </Carte>
        ))}
      </section>
    </div>
  );
}
